<script lang="typescript">
  // packages
  import {slugify} from '../utils/slugify'
  import { formatPercent } from '../utils/format.js';
  import { intcomma, pluralize } from 'journalize';
  import Photos from './Photos.svelte';
  import { choiceName, partyClass } from '../utils/race-helpers.js';

  // types
  import type { Readable } from 'svelte/store';
  import type { Race, Choice, ChoiceLookup, CrosswalkImage } from '../types';

  export let race: Readable<Race>;
  /** choiceIds, e.g. '94' */
  export let candidate1: string;
  export let candidate2: string;
  export let candidateLookup: Readable<ChoiceLookup>;
  export let crosswalk: Record<string, CrosswalkImage> = {};
  export let hp: boolean = false;

  // find the candidates; both ids are set in embeds-ca-gov.js and must be in the race
  $: candidate_1 = $candidateLookup.get(candidate1) as Choice;
  $: candidate_2 = $candidateLookup.get(candidate2) as Choice;

  // candidate headshots
  $: candidate_1_img = crosswalk[candidate1] ?? crosswalk.person;
  $: candidate_2_img = crosswalk[candidate2] ?? crosswalk.person;

  // calculating votes
  $: totalVotes = $race.attributes.totalVotes ?? 0;
  // strings for top number
  $: candidate1PctString = formatPercent(candidate_1.votes, totalVotes);
  $: candidate2PctString = formatPercent(candidate_2.votes, totalVotes);
  // pct as a number for element width
  $: candidate1Width =
    candidate_1.votes > 0 && totalVotes > 0
      ? (candidate_1.votes / totalVotes) * 100
      : 0;
  $: candidate2Width =
    candidate_2.votes > 0 && totalVotes > 0
      ? (candidate_2.votes / totalVotes) * 100
      : 0;
  $: remainingWidth =
    candidate1Width >= 0 && candidate2Width >= 0
      ? 100 - (candidate1Width + candidate2Width)
      : 100;

  export function findParty(c: Choice) {
    let party = c.party?.name;
    if (party == 'Democrat') {
      return 'color-dem-dark';
    } else if (party == 'Republican') {
      return 'color-gop-dark';
    }
  }

    // candidate 1 and 2 are set by the embed (e.g. embeds-ca-gov.js). This is not sorted.
    function determineLeadingText(c1: Choice, c2: Choice, totalVotes: number){
      // choiceName, not title: L.A. Vote candidates have no title
      const c1Name = c1.last;
      const c2Name = c2.last;
      let c1_pct = (c1.votes/totalVotes)*100;
      let c2_pct = (c2.votes/totalVotes)*100;
      if (c1.result === 'winner') {
        return `The race has been called for ${c1Name} with ${c1_pct.toFixed(1)}% of the votes so far. ${c2Name} has ${c2_pct.toFixed(1)}% of votes.`
      } else if (c2.result === 'winner') {
        return `The race has been called for ${c2Name} with ${c2_pct.toFixed(1)}% of the votes so far. ${c1Name} had ${c1_pct.toFixed(1)}% of votes.`
      } else if ((c1.votes == 0) && (c2.votes == 0)){ // no votes
        return `There are no results yet.`
      } else if (c1_pct > c2_pct) { // if candidate 1 leads
        return `${c1Name} leads with ${c1_pct.toFixed(1)}% of the vote. ${c2Name} has ${c2_pct.toFixed(1)}%.`
      } else if (c1_pct < c2_pct) { // if candidate 2 leads
        return `${c2Name} leads with ${c2_pct.toFixed(1)}% of the vote. ${c1Name} has ${c1_pct.toFixed(1)}%.`
      } else if ((c1_pct === c2_pct) && (c1_pct > 0) && (c2_pct > 0)){
        return `${c2Name} and ${c1Name} are tied with ${c2_pct.toFixed(1)}% of votes.`
      } else {
        return ''
      }
    }

  function describeCandidate(c: Choice) {
    const party = c.party?.abbr !== 'NP' ? c.party?.name : '';
    const name = party ? `${party} ${choiceName(c)}` : choiceName(c);
    return c.isIncumbent ? `${name}, the incumbent` : name;
  }

  // race title from the feed, e.g. 'Governor' or 'Los Angeles Mayor'.
  // The comma after candidate 1 closes ", the incumbent," mid-sentence.
  $: ariaLabel = `Bar chart shows the results from the race for ${$race.attributes.title} between ${describeCandidate(candidate_1)}${candidate_1.isIncumbent ? ',' : ''} and ${describeCandidate(candidate_2)}. Either candidate needs at least 50% to win. ${determineLeadingText(candidate_1, candidate_2, totalVotes)}`;
</script>

<!-- <div
  class="race-embed-container"
  class:display-grid={multiple}
  class:grid-cols-2={multiple}
  class:grid-gap-3={multiple}
  data-type={component}
>
</div> -->

<div
  role="img"
  class="display-flex w-full position-relative text-sans-serif space-y-n2"
  aria-label={ariaLabel}
>
  <div class="display-flex color-{partyClass(candidate_1)}-dark image-style">
    <div class="duo-head left candidate-{partyClass(candidate_1)}-headshot">
      <Photos
        name={choiceName(candidate_1)}
        src={candidate_1_img.jpg || candidate_1_img.png}
        avif={candidate_1_img.avif}
        webp={candidate_1_img.webp}
        party={partyClass(candidate_1)}
      />
    </div>
  </div>

  <div style="flex:1;">
    <dl class="display-flex justify-between leading-none text-medium mb-14">
      <div class="text-left {!findParty(candidate_1) ? '' : findParty(candidate_1)}" style="width: 40%;">
        <div class="{!hp ? 'text-5xl' : 'text-4xl'} mb-11">{candidate1PctString}%</div>
        <dt class="balance-name text-2xl">
          {(hp == true) ? candidate_1.last: choiceName(candidate_1)}{#if candidate_1.isIncumbent}<span
              title="Currently holds this office"
              role="img"
              aria-label="incumbent">*</span
            >{/if}{#if candidate_1.result == "winner"}
            <span class="candidate-checkmark" aria-label="checkmark, winner"
              >✓</span
            >{/if}
        </dt>
      </div>

      <div class="text-center to-win-text text-bold">50%</div>

      <div class="text-right {!findParty(candidate_2) ? '' : findParty(candidate_2)}" style="width: 40%;">
        <div class="{!hp ? 'text-5xl' : 'text-4xl'} mb-11">{candidate2PctString}%</div>
        <dt class="balance-name text-2xl">
          {#if candidate_2.result == "winner"}<span
              class="candidate-checkmark"
              aria-label="checkmark, winner">✓</span
            >{/if}
          {(hp == true) ? candidate_2.last : choiceName(candidate_2)}{#if candidate_2.isIncumbent}<span
              title="Currently holds this office"
              role="img"
              aria-label="incumbent">*</span
            >{/if}
        </dt>
      </div>
    </dl>

    <div class="position-relative">
      <div class="display-flex w-full bar-height">
        <div
          class="bg-{(candidate_1.party?.abbr !== 'NP') ? candidate_1.party?.abbr : slugify(choiceName(candidate_1))} l-bar"
          style="width: {candidate1Width}%"
        />
        {#if remainingWidth > 0}
          <div class="bg-neutral g-bar" style="width: {remainingWidth}%" />
        {/if}
        <div
          class="bg-{(candidate_2.party?.abbr !== 'NP') ? candidate_2.party?.abbr : slugify(choiceName(candidate_2))} r-bar"
          style="width: {candidate2Width}%"
        />
      </div>

      <div
        class="display-flex justify-center w-full position-absolute top-0 bar-height"
      >
        <div class="balance-bar" />
      </div>

      <div class="display-flex justify-between text-sm color-grey mt-8">
        <dt class="balance-name">
          <p class="vote-totals color-grey m-0">
            {intcomma(candidate_1.votes)}
            vote{pluralize(candidate_1.votes)}
          </p>
        </dt>
        <dt class="balance-name">
          <p class="vote-totals color-grey m-0">
            {intcomma(candidate_2.votes)}
            vote{pluralize(candidate_2.votes)}
          </p>
        </dt>
      </div>
    </div>
  </div>

  <div class="display-flex color-{partyClass(candidate_2)}-dark image-style">
    <div class="duo-head right candidate-{partyClass(candidate_2)}-headshot">
      <Photos
        name={choiceName(candidate_2)}
        src={candidate_2_img.jpg || candidate_2_img.png}
        avif={candidate_2_img.avif}
        webp={candidate_2_img.webp}
        party={partyClass(candidate_2)}
      />
    </div>
  </div>
</div>
